import {
  EFeatureFlag,
  HTTP_RESPONSES,
} from '@/constant/global/global.constant';
import { VDI_VERSION } from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import axios from 'axios';
import { MAGIC_COOKIES } from '@/pages/StandAlone/constants';
import { getCookie } from '@/standalone';
import { API_URL_CONFIG } from './urlConfig/apiUrlConfig';

// Check if running in standalone mode (no auth redirect)
const isStandaloneMode = () => {
  // Check for standalone params in cookie (tab-specific)
  const hasSid = getCookie(MAGIC_COOKIES.SID);
  const hasSig = getCookie(MAGIC_COOKIES.SIG);
  return !!(hasSid || hasSig);
};

/**
 * @abstract VDI
 */
const localStorageToken = localStorage.getItem('access_token');
const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/${import.meta.env.VITE_VDI_PATH}`,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: `Bearer ${localStorageToken}`,
    mode: 'no-cors',
  },
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  },
);
axiosInstance.interceptors.request.use((config) => {
  // Local Server Enabled
  const host =
    (import.meta.env.VITE_LOCAL_SERVER === EFeatureFlag.ENABLED
      ? config.params?.version === VDI_VERSION[0].id
        ? import.meta.env.VITE_VDI_V1_BASE_URL
        : import.meta.env.VITE_VDI_V2_BASE_URL
      : import.meta.env.VITE_BASE_URL) || import.meta.env.VITE_BASE_URL;
  const basePath = `${host}/${import.meta.env.VITE_VDI_PATH}`;
  const version =
    config.params?.version === VDI_VERSION[0].id
      ? VDI_VERSION[0].id
      : VDI_VERSION[1].id; // Assuming version is passed as a parameter
  config.baseURL = `${basePath}/${version}`; // Set base URL for v2

  // You can also remove the version parameter if it's not needed in the request
  delete config.params.version;

  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      console.error('Network Error: No response received.');
    } else {
      const code = error.response.status;
      const response = error.response.data;
      const originalRequest = error.config;

      if (
        code === HTTP_RESPONSES.AUTHENTICATION_FAILED.code &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (!isStandaloneMode()) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/';
        }
      } else if (code === HTTP_RESPONSES.AUTHORIZATION_FAILED.code) {
        if (!isStandaloneMode()) {
          window.location.href = '/forbidden';
        }
      }

      return Promise.reject(error);
    }
  },
);

export default axiosInstance;

// If 401 errror occurs, use this token to login
export const axiosInventoryInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/${import.meta.env.VITE_INVENTORY_PATH}`,
  // baseURL:  `${import.meta.env.VITE_LOCAL_BASE_URL}/`,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: `Bearer ${localStorageToken}`,
    mode: 'no-cors',
  },
});

axiosInventoryInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInventoryInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      console.error('Network Error: No response received.');
    } else {
      if (isStandaloneMode()) {
        return Promise.reject(error);
      }
      const code = error.response.status;
      const response = error.response.data;
      const originalRequest = error.config;

      if (
        (code === HTTP_RESPONSES.NOT_FOUND.code ||
          code === HTTP_RESPONSES.UNPROCESSABLE_CONTENT.code) &&
        error?.response?.request?.responseURL.includes(
          API_URL_CONFIG.RECONCILIATION.GET_DOWNLOAD_REPORT,
        )
      ) {
        return { status: code, message: response.message };
      }

      if (
        code === HTTP_RESPONSES.AUTHENTICATION_FAILED.code &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (!isStandaloneMode()) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/';
        }
      } else if (code === HTTP_RESPONSES.AUTHORIZATION_FAILED.code) {
        if (!isStandaloneMode()) {
          window.location.href = '#/forbidden';
        }
      }

      return Promise.reject(error);
    }
  },
);

export const axiosAlertInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/${import.meta.env.VITE_ALERT}`,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: `Bearer ${localStorageToken}`,
    mode: 'no-cors',
  },
});

axiosAlertInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosAlertInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      console.error('Network Error: No response received.');
    } else {
      const code = error.response.status;
      const response = error.response.data;
      const originalRequest = error.config;

      if (
        code === HTTP_RESPONSES.AUTHENTICATION_FAILED.code &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (!isStandaloneMode()) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/';
        }
      } else if (code === HTTP_RESPONSES.AUTHORIZATION_FAILED.code) {
        if (!isStandaloneMode()) {
          window.location.href = '#/forbidden';
        }
      }

      return Promise.reject(error);
    }
  },
);

export const axiosSmartCoolersInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/v1/smartcoolers`,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: `Bearer ${localStorageToken}`,
    mode: 'no-cors',
  },
});

axiosSmartCoolersInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosSmartCoolersInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      console.error('Network Error: No response received.');
    } else {
      const code = error.response.status;
      const response = error.response.data;
      const originalRequest = error.config;

      if (
        code === HTTP_RESPONSES.AUTHENTICATION_FAILED.code &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (!isStandaloneMode()) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/';
        }
      } else if (code === HTTP_RESPONSES.AUTHORIZATION_FAILED.code) {
        if (!isStandaloneMode()) {
          window.location.href = '/forbidden';
        }
      }

      return Promise.reject(error);
    }
  },
);
